import { Link } from "react-router-dom";
import { useCart } from "../lib/CartContext";
import { resolveImageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Trash2, ArrowRight, ShoppingCart } from "lucide-react";

export default function CartPage() {
    const { items, removeItem, subtotal, shippingCost } = useCart();
    const total = subtotal + shippingCost;

    return (
        <div className="min-h-[70vh]" data-testid="cart-page">
            <div className="bg-card border-b border-border">
                <div className="container mx-auto px-6 lg:px-12 py-12 lg:py-16">
                    <h1 className="font-heading text-4xl sm:text-5xl font-black text-foreground tracking-tight">
                        Your Cart
                    </h1>
                </div>
            </div>

            <div className="container mx-auto px-6 lg:px-12 py-12">
                {items.length === 0 ? (
                    <div className="text-center py-20">
                        <ShoppingCart className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                        <h2 className="font-heading text-lg font-bold text-foreground mb-2">Your cart is empty</h2>
                        <p className="text-muted-foreground text-sm mb-6">Browse our equipment and add items to get started.</p>
                        <Link to="/products">
                            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider px-8">
                                Browse Equipment
                            </Button>
                        </Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-4">
                            {items.map((item) => (
                                <div key={item.product_id} className="bg-card border border-border p-4 flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                                    {item.image_url ? (
                                        <img src={resolveImageUrl(item.image_url)} alt={item.product_name} className="w-24 h-24 object-contain bg-muted/30" />
                                    ) : (
                                        <div className="w-24 h-24 bg-muted/30 flex items-center justify-center text-xs text-muted-foreground">No img</div>
                                    )}
                                    <div className="flex-1">
                                        <Link to={`/products/${item.product_id}`} className="font-heading font-bold text-foreground hover:text-primary transition-colors">
                                            {item.product_name}
                                        </Link>
                                        <p className="text-muted-foreground text-xs mt-1">{item.manufacturer}</p>
                                        <p className="text-primary font-bold text-lg mt-2">${item.price.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                                        <p className="text-muted-foreground text-xs mt-1">+ ${(item.shipping_cost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} {item.shipping_label || "Shipping"}</p>
                                    </div>
                                    <button onClick={() => removeItem(item.product_id)} className="text-muted-foreground hover:text-destructive transition-colors self-start" data-testid={`remove-item-${item.product_id}`}>
                                        <Trash2 className="h-5 w-5" />
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="bg-card border border-border p-6 h-fit sticky top-24">
                            <h2 className="font-heading font-bold text-lg text-foreground mb-4">Order Summary</h2>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Subtotal</span>
                                    <span className="text-foreground font-bold">${subtotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Shipping ({items.length} item{items.length > 1 ? "s" : ""})</span>
                                    <span className="text-foreground font-bold">${shippingCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div className="border-t border-border pt-3 flex justify-between">
                                    <span className="text-foreground font-bold text-base">Total</span>
                                    <span className="text-primary font-bold text-xl">${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                </div>
                            </div>
                            <p className="text-muted-foreground text-xs mt-4">
                                Insured nationwide shipping to the 48 lower states. Shipping charged per item.
                            </p>
                            <Link to="/checkout" className="block mt-6">
                                <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider py-6" data-testid="proceed-checkout-btn">
                                    Proceed to Checkout <ArrowRight className="ml-2 h-5 w-5" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
